/**
 *
 * MessageContainer
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import TextMessage from 'ui/components/TextMessage';
import ButtonsMessage from 'ui/components/ButtonsMessage';
import InputMessage from 'ui/components/InputMessage';
import RatingCard from 'ui/components/RatingCard';
import LinkMessage from 'ui/components/LinkMessage';
import Calendar from 'ui/components/Calendar';
import Avatar from 'react-avatar';
import { slideUp } from 'styles/animation';
import Moment from 'react-moment';
import shortMomentString from 'utils/shortMomentString';
import { MOMENT_TIME_LIMIT, CALENDAR_STRINGS } from 'common/constants';
import EventMessage from 'ui/components/EventMessage';
import AttachmentMessage from "../AttachmentMessage";
import {ReactComponent as RetryIcon} from 'assets/icons/retry-icon.svg';
import theme from "../../../styles/theme";
import InsentAvatar from "../Avatar";

const SenderImage = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 100%;
`;

const GifImage = styled.img`
  max-width: 240px;
  border: 2px solid #DADDE4;
  border-radius: 16px 16px 16px 6px;
  display : block;
`;

const SenderImageWrapper = styled.div`
  margin-top: 10px;
  opacity: 0;
  transform: translateY(10px);
  will-change: transform;
  animation: ${slideUp} 0.4s ease-in-out 0.1s forwards;
  & > div {
    background-color: ${p => p.bgColor ? p.bgColor+" !important" : ''};
    padding: 3px;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  margin: ${p => p.margin};
  ${p =>
    p.type === 'event' && 'justify-content: center; flex-direction: row;align-items: center;  '}
`;

const MessageElementWrapper = styled(({ lead, leftSpace, messageType, isCalendar, children, ...others }) => (
  <div {...others}>{children}</div>
))`
  float: ${p => (p.lead ? 'right' : 'left')};
  margin: 0;
  margin-bottom: ${p => (p.messageType === 'buttons' ? '0' : (p.messageType === "attachments" || p.messageType === "attachmentsArray" ? '10px' : '5px'))};
  margin-top: ${p => (p.messageType === 'buttons' ? '0' : '5px')};
  margin-left: ${p => (p.lead ? 'auto' : p.isCalendar ? '0' : p.leftSpace ? '40px' : '10px')};
  ${p => p.messageType === 'event' || (p.messageType !== 'text' && p.messageType !=="attachmentsArray" && 'width: 100%;')}
`;

const NewSessionTime = styled.div`
  width: 75%;
  text-align: center;
  font-size: 9px;
  color: ${p => p.theme.palette.grey3};
  opacity: 0;
  transform: translateY(10px);
  animation: ${slideUp} 0.2s ease-in-out 0.1s forwards;
  will-change: transform;
  font-style: italic;
  line-height: 11px;
  margin-left: calc(1rem + 42px);
  ${p => p.lead && 'text-align: right; padding-right: 0.25rem'}
  ${p => p.previousMessage && 'margin-top: 16px'}
  margin-bottom: 16px;
  margin-right: 1rem;
`;

const AttachmentArrayContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SenderDetails = styled.div`
  font-size: 9px;
  color: ${p => p.theme.palette.grey3};
  opacity: 0;
  transform: translateY(10px);
  animation: ${slideUp} 0.2s ease-in-out 0.1s forwards;
  will-change: transform;
  font-style: italic;
  line-height: 11px;
  margin-left: calc(1rem + 42px);
  ${p => p.lead && 'text-align: right; padding-right: 0.25rem'}
  margin-right: 1rem;
`;

const RetryAttachment = styled.div`
  font-size: 9px;
  color: #2F3C4E;
  opacity: 0.5;
  font-style: italic;
  line-height: 11px;
  ${p => p.lead && 'text-align: right; padding-right: 0.25rem'}
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-bottom: 1rem;
  align-items: center;
  cursor: pointer;
`;

const RetryText = styled.p`
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  margin: 0 1.5rem 0 3px;
`

export const MessageContainer = ({
  userSettingColor,
  userSettings,
  prevMessage,
  Language,
  message,
  processUserResponse,
  sending,
  item,
  userConfig,
  fetchMeetingSlots,
  bookMeetingSlot,
  conversationListRef,
  currentChannel,
  handleHyperLinkRedirects,
  previewRetry,
  setPreviewRetry,
  onClickRetry,
  messageKey
}) => {
  let messageType = message && message.type;
  let newSession = false;
  if(!messageType && message.newSession === true)
    newSession =true;
  let leftSpace = false;
  let isCalendarLeftSpace = false;
  if (prevMessage && prevMessage?.hasOwnProperty('feedback')) {
    const prevMessageCopy = { ...prevMessage };
    delete prevMessageCopy.name;
  }
  if (
    prevMessage &&
    prevMessage.newSession !== true &&
    message.name === prevMessage.name &&
    !message.lead &&
    prevMessage.event === undefined
  ) {
    leftSpace = true;
  }
  if (
    prevMessage &&
    message.name === prevMessage.name &&
    !message.lead &&
    prevMessage.event === undefined
  ) {
    isCalendarLeftSpace = true;
  }
  const [showTime, setShowTime] = React.useState(false);
  const toggleShowTime = () => {
    setShowTime(!showTime);
  };

  const lastMessage = item &&  (item.itemIndex === item.total - 1);

  if (messageType === 'feedback') {
    message.lead = true;
  }

  if(prevMessage && message && prevMessage.newSession === true && message.newSession === true)
    return;

  let isCalendar = messageType === 'calendar';
  return (
    <div key={messageKey}>
      <MessageWrapper id="insent-message-container-message-wrapper" type={messageType} onClick={newSession ? undefined : toggleShowTime} margin={isCalendar ? '0 10px' : '0 1rem'}>
        {!leftSpace && message && !newSession && !message.lead && messageType !== 'event' && messageType !== 'buttons' && messageType !== 'feedback' && !isCalendar && (
          <SenderImageWrapper bgColor={userSettingColor ? userSettingColor.headerBackgroundColor : theme.palette.primary} id="insent-message-container-sender-image-wrapper">
            <Avatar id="insent-message-container-avatar" src={message.userId==="bot"?(userSettings.bot.overrideLogo?userSettings.bot.overrideLogo:message.img):message.img} name={message.name || 'Agent'} size="27" round={true} />
          </SenderImageWrapper>
        )}
        {newSession === true && (
            <NewSessionTime previousMessage={prevMessage} lead={message.lead}>
              <Moment calendar={CALENDAR_STRINGS} interval={10000} unix>
                {message.time}
              </Moment>
            </NewSessionTime>
        )}
        {messageType === 'feedback' && (
          <MessageElementWrapper
              id="insent-message-container-message-wrapper"
            lead={true}
            leftSpace={leftSpace}
            messageType={messageType}
          >
            <RatingCard
              id="insent-message-container-rating-card"
              userSettingColor={userSettingColor}
              Language={Language}
              message={message}
              processUserResponse={processUserResponse}
            />
          </MessageElementWrapper>

        )}
        {messageType === "attachments" && !Array.isArray(message.attachments) &&
          <MessageElementWrapper
              id="insent-message-container-message-wrapper"
              lead={message.lead}
              leftSpace={leftSpace}
              messageType={messageType}
          >
            <AttachmentMessage
              message={message}
              sender={message.lead ? 'client' : 'server'}
              userSettingColor={userSettingColor}
            />
          </MessageElementWrapper>
        }
        {
          messageType === "attachments" && message.isGif &&
          <MessageElementWrapper
              id="insent-message-container-message-wrapper"
              lead={message.lead}
              leftSpace={leftSpace}
              messageType={messageType}
          >
            <AttachmentMessage
                message={{...message, attachments: message.gifs}}
                sender={message.lead ? 'client' : 'server'}
                userSettingColor={userSettingColor}
            />
          </MessageElementWrapper>
        }
        {messageType === "attachments" && Array.isArray(message.attachments) && message.attachments.length > 0 &&
          <AttachmentArrayContainer>
              {
                message.attachments.map(element => {
                let newMessage = {...message};
                newMessage.attachments = [element];
                return (
                  <MessageElementWrapper
                      id="insent-message-container-message-wrapper"
                      lead={newMessage.lead}
                      leftSpace={leftSpace}
                      messageType={"attachmentsArray"}
                  >
                    <AttachmentMessage
                        message={newMessage}
                        sender={newMessage.lead ? 'client' : 'server'}
                        userSettingColor={userSettingColor}
                    />
                  </MessageElementWrapper>
                )
                })
              }
            </AttachmentArrayContainer>
        }
        {messageType === 'text' && (
          <MessageElementWrapper
              id="insent-message-container-message-wrapper"
            lead={message.lead}
            leftSpace={leftSpace}
            messageType={messageType}
          >
            <TextMessage
              id="insent-message-container-text-message"
              Language={Language}
              userSettingColor={userSettingColor}
              sender={message.lead ? 'client' : 'server'}
              text={message.text}
              keyValues={message.keyValues}
              category={message.category}
              mentions={message.mentions}
              actualMessage={message}
              sending={sending}
              userConfig={userConfig}
              currentChannel={currentChannel}
            />
          </MessageElementWrapper>
        )}
        {messageType === 'buttons' && (
          <MessageElementWrapper
              id="insent-message-container-message-wrapper"
            lead={message.lead}
            leftSpace={leftSpace}
            messageType={messageType}
          >
            <ButtonsMessage
              id="insent-message-container-buttons-message"
              userSettingColor={userSettingColor}
              message={message}
              processUserResponse={processUserResponse}
              handleHyperLinkRedirects={handleHyperLinkRedirects}
            />
          </MessageElementWrapper>
        )}

        {messageType === 'input' && (
          <MessageElementWrapper
              id="insent-message-container-message-wrapper"
            lead={message.lead}
            leftSpace={leftSpace}
            messageType={messageType}
          >
            <InputMessage
              id="insent-message-container-input-message"
              Language={Language}
              userSettingColor={userSettingColor}
              message={message}
              processUserResponse={processUserResponse}
            />
          </MessageElementWrapper>
        )}

        {messageType === 'link' && (
          <MessageElementWrapper
              id="insent-message-container-message-wrapper"
            lead={message.lead}
            leftSpace={leftSpace}
            messageType={messageType}
          >
            <LinkMessage
              id="insent-message-container-link-message"
              message={message}
              userSettingColor={userSettingColor}
              userConfig={userConfig}
              currentChannel={currentChannel}
            />{' '}
          </MessageElementWrapper>
        )}

        {messageType === 'calendar' && (
          <MessageElementWrapper
            id="insent-message-container-message-wrapper"
            lead={message.lead}
            leftSpace={leftSpace}
            messageType={messageType}
            isCalendar={isCalendar}
          >
            <Calendar
              id="insent-message-container-calendar"
              userSettingColor={userSettingColor}
              userSettings={userSettings}
              Language={Language}
              message={message}
              fetchMeetingSlots={fetchMeetingSlots}
              bookMeetingSlot={bookMeetingSlot}
              processUserResponse={processUserResponse}
              isCalendarLeftSpace={isCalendarLeftSpace}
              conversationListRef={conversationListRef}
              userConfig={userConfig}
              currentChannel={currentChannel}
            />
          </MessageElementWrapper>
        )}
        {message?.gif && Array.isArray(message.gif) && (
            <MessageElementWrapper
                id="insent-message-container-message-wrapper"
                lead={message.lead}
                leftSpace={leftSpace}
                messageType={messageType}
                isCalendar={isCalendar}
            >
              <GifImage src={message?.gif[0]?.url} alt={message?.gif[0]?.name}/>
            </MessageElementWrapper>
        )}
        {messageType === 'event' && (
            <EventMessage
                message={message}
                userSettingColor={userSettingColor}
                Language={Language}
            />
        ) }

      </MessageWrapper>
      {
        previewRetry && message && messageType==="attachments" && message.preview &&
      <RetryAttachment lead={message.lead} onClick={onClickRetry}>
        <RetryIcon/>
        <RetryText>Retry</RetryText>
      </RetryAttachment>
      }
      {(lastMessage || showTime) && messageType !== 'buttons' && !isCalendar && newSession !== true && (
        <SenderDetails id="insent-message-container-sender-details" lead={message.lead}>
          <Moment ago interval={10000} fromNow unix filter={shortMomentString}>
            {message.time > MOMENT_TIME_LIMIT ? message.time / 1000 : message.time}
          </Moment>
        </SenderDetails>
      )}
    </div>
  );
};

MessageContainer.propTypes = {
  userSettingColor: PropTypes.object,
  userSettings: PropTypes.object,
  prevMessage: PropTypes.object,
  message: PropTypes.object,
  messageType: PropTypes.string,
  processUserResponse: PropTypes.func,
};

export default MessageContainer;
